import { LocationCard } from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Image,
  Separator,
} from '@repo/shared-client/components/ui'
import { STATIC_MINIMAP_RANGE_KM } from '@repo/shared-client/mapbox'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { useTranslation } from '@web/src/lib/intl/react'
import { InfoIcon } from 'lucide-react'
import { useRef } from 'react'
import SFRichTextViewer from 'src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import { useAuth } from '../../../hooks/use-auth'

interface EventDetailsProps {
  publicAccessMode?: boolean
  event: {
    id: string
    distance?: number
    description?: string
    privateDescription?: string
    privateAdmissionNote?: string
    location?: string
    locationExact?: boolean
    locationLat?: number
    locationLong?: number
    privateLocation?: string
    privateLocationLat?: number
    privateLocationLong?: number
    canSeePrivate?: boolean
    canSeeAttendees?: boolean
  }
}

const EventDetails = ({ event }: EventDetailsProps) => {
  const { t } = useTranslation()
  const { isAuthenticated, hasPermission } = useAuth()
  const { mapBoxToken } = useMapBox()

  const showPrivateLocation = Boolean(event.canSeePrivate && event.privateLocation)
  const locationCardLocation = showPrivateLocation
    ? {
        location: event.privateLocation,
        locationExact: true,
        locationLat: event.privateLocationLat,
        locationLong: event.privateLocationLong,
        distance: event.distance ?? undefined,
      }
    : {
        location: event.location,
        locationExact: event.locationExact,
        locationLat: event.locationLat,
        locationLong: event.locationLong,
        distance: event.distance ?? undefined,
      }

  const canViewMap = hasPermission('location:view-map') && Boolean(mapBoxToken)
  const distanceRaw = locationCardLocation.distance
  const distanceKm = distanceRaw != null && Number.isFinite(distanceRaw) ? Math.round(distanceRaw) : 0
  const locationDisplayText = locationCardLocation.locationExact
    ? (locationCardLocation.location ?? '')
    : t('Near {{location}}', { location: locationCardLocation.location ?? '' })
  const latitude = locationCardLocation.locationLat ?? 0
  const longitude = locationCardLocation.locationLong ?? 0
  const mapRangeKm = canViewMap ? (locationCardLocation.locationExact ? undefined : STATIC_MINIMAP_RANGE_KM) : undefined

  const content = useRef(null)
  const privateContent = useRef(null)
  const privateDescriptionAvailable =
    isAuthenticated && event.canSeePrivate && event.privateDescription && event.privateDescription !== '<p></p>'

  const publicLineClamp = privateDescriptionAvailable ? 8 : 18
  const privateLineClamp = 9

  return (
    <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-[3fr_2fr]">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t('Event Details')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex w-full flex-col gap-3">
            <SFRichTextViewer
              className="w-full p-0"
              content={event.description}
              editorStyles="overflow: hidden"
              lineClamp={publicLineClamp}
              ref={content}
            />

            {privateDescriptionAvailable && (
              <>
                <Separator />
                <p className="font-bold">{t('Private Event Details:')}</p>
                <Alert variant="destructive">
                  <InfoIcon className="text-destructive" />
                  <AlertDescription>{t('Please do not share with others outside of the event.')}</AlertDescription>
                </Alert>
                <SFRichTextViewer
                  className="w-full p-0"
                  content={event.privateDescription}
                  editorStyles="overflow: hidden"
                  lineClamp={privateLineClamp}
                  ref={privateContent}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {isAuthenticated ? (
        <div className="flex flex-col gap-3 md:self-start">
          <LocationCard
            distanceKm={distanceKm}
            isPrivate={showPrivateLocation}
            latitude={latitude}
            location={locationDisplayText}
            longitude={longitude}
            mapboxToken={mapBoxToken}
            rangeKm={mapRangeKm}
            showSupporterPlaceholder={!canViewMap}
          />
        </div>
      ) : (
        <div className="relative max-w-[400px] overflow-clip rounded-md border border-border">
          <Image alt={t('Map available to members only')} className="w-full object-cover" src="/map-members.webp" />
        </div>
      )}
    </div>
  )
}

export default EventDetails
